import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d73ifjpttkj2t7sc9q4g';

export default function ContactSection() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setLoading(true);
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref as React.RefObject<HTMLElement>}
      className={`w-full bg-white py-24 lg:py-32 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left Column */}
          <div className="w-full lg:w-5/12">
            <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              {t('contact_eyebrow')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] leading-tight mb-8">
              {t('contact_h2_line1')}<br />
              <span className="text-[#C9A84C]">{t('contact_h2_line2')}</span>
            </h2>

            <div className="space-y-6">
              {[
                { icon: 'ri-phone-line', labelKey: 'contact_phone_label', value: '+507 6000-0000' },
                { icon: 'ri-mail-line', labelKey: 'contact_email_label', value: 'info@phoenixtransportation.pa' },
                { icon: 'ri-map-pin-2-line', labelKey: 'contact_route_label', value: 'Panama City ↔ Chitré, Panama' },
                { icon: 'ri-time-line', labelKey: 'contact_hours_label', valueKey: 'contact_hours_val' },
              ].map((item) => (
                <div key={item.labelKey} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#C9A84C]/10 rounded-xl shrink-0">
                    <i className={`${item.icon} text-[#C9A84C] text-xl`} />
                  </div>
                  <div>
                    <p className="text-[#999] text-xs uppercase tracking-widest mb-1">{t(item.labelKey)}</p>
                    <p className="text-[#1a1a1a] font-semibold">
                      {item.valueKey ? t(item.valueKey) : item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="mt-10 flex items-center gap-4">
              <a href="https://facebook.com" rel="nofollow" target="_blank" className="w-10 h-10 flex items-center justify-center border border-[#C9A84C]/40 rounded-full text-[#C9A84C] hover:bg-[#C9A84C] hover:text-white transition-all duration-300 cursor-pointer">
                <i className="ri-facebook-fill text-lg" />
              </a>
              <a href="https://instagram.com" rel="nofollow" target="_blank" className="w-10 h-10 flex items-center justify-center border border-[#C9A84C]/40 rounded-full text-[#C9A84C] hover:bg-[#C9A84C] hover:text-white transition-all duration-300 cursor-pointer">
                <i className="ri-instagram-line text-lg" />
              </a>
              <a href="https://wa.me/50760000000" rel="nofollow" target="_blank" className="w-10 h-10 flex items-center justify-center border border-[#C9A84C]/40 rounded-full text-[#C9A84C] hover:bg-[#C9A84C] hover:text-white transition-all duration-300 cursor-pointer">
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="w-full lg:w-7/12">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16 bg-[#F8F5F0] rounded-2xl px-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#C9A84C]/15 rounded-full mb-6">
                  <i className="ri-check-line text-[#C9A84C] text-3xl" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-3">{t('contact_success_title')}</h3>
                <p className="text-[#777] text-base max-w-sm">{t('contact_success_desc')}</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="phoenix-contact-form"
                onSubmit={handleSubmit}
                className="bg-[#F8F5F0] rounded-2xl p-8 md:p-10 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                      {t('contact_form_fullName')} <span className="text-[#C9A84C]">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder={t('contact_form_fullName_ph')}
                      className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                      {t('contact_form_email')} <span className="text-[#C9A84C]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder={t('contact_form_email_ph')}
                      className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                      {t('contact_form_phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t('contact_form_phone_ph')}
                      className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                      {t('contact_form_travelDate')}
                    </label>
                    <input
                      type="date"
                      name="travel_date"
                      className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                    {t('contact_form_route')} <span className="text-[#C9A84C]">*</span>
                  </label>
                  <select
                    name="route"
                    required
                    className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200 cursor-pointer"
                  >
                    <option value="">{t('contact_form_route_ph')}</option>
                    <option value="Panama City to Chitré">{t('contact_form_route_opt1')}</option>
                    <option value="Chitré to Panama City">{t('contact_form_route_opt2')}</option>
                    <option value="Round Trip">{t('contact_form_route_opt3')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#555] text-xs font-semibold uppercase tracking-wider mb-2">
                    {t('contact_form_message')}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder={t('contact_form_message_ph')}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200 resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-red-500' : 'text-[#aaa]'}`}>
                    {charCount}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C9A84C] hover:bg-[#b8953e] disabled:opacity-60 text-white font-semibold text-sm tracking-wider uppercase px-6 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin" /> {t('contact_form_sending')}</>
                  ) : (
                    <><span>{t('contact_form_submit')}</span><i className="ri-arrow-right-line" /></>
                  )}
                </button>
                <p className="text-center text-[#aaa] text-xs">{t('contact_form_note')}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
